/* eslint-disable perfectionist/sort-classes, typescript-extended-rules/file-naming-convention */
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
	BeneficiaryReconciliationApplyAction,
	BeneficiaryReconciliationApplyTargetValue,
	BeneficiaryReconciliationCandidateMatch,
	BeneficiaryReconciliationField,
	BeneficiaryReconciliationItem,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';
import { TypeKinship } from '../../../../../shared/enums/type-kinship.enum';
import { getBeneficiaryReconciliationFieldLabel } from '../beneficiary-reconciliation-field-metadata';

interface BeneficiaryReconciliationInlineResolutionSavedResult {
	action: BeneficiaryReconciliationApplyAction;
	applyOnly?: boolean;
	definitiveIgnore?: boolean;
	onHold?: boolean;
	targetValue: BeneficiaryReconciliationApplyTargetValue;
}

export type BeneficiaryReconciliationInlineResolutionResult =
	| { removePending: true }
	| BeneficiaryReconciliationInlineResolutionSavedResult;

interface DuplicateRow {
	beneficiaryCpf?: string | null;
	beneficiaryName?: string | null;
	cardNumber?: string | null;
	carrier?: string | null;
	holderCpf?: string | null;
	organizationName?: string | null;
	parentesco?: string | null;
	planName?: string | null;
	rowNumber?: number | null;
	sheetName?: string | null;
	sourceFileName?: string | null;
	titularity?: string | null;
	uid?: string | null;
}

interface InlineResolutionDialogData {
	item: BeneficiaryReconciliationItem;
	pendingChange: BeneficiaryReconciliationInlineResolutionSavedResult | null;
}

interface CreationDataField {
	group: 'complementary' | 'essential';
	isMissing: boolean;
	label: string;
	value: string;
}

@Component({
	selector: 'app-beneficiary-reconciliation-inline-resolution-modal',
	styleUrls: [
		'./beneficiary-reconciliation-inline-resolution-modal.component.scss',
	],
	templateUrl:
		'./beneficiary-reconciliation-inline-resolution-modal.component.html',
})
export class BeneficiaryReconciliationInlineResolutionModalComponent {
	@ViewChild('duplicateLayoutCapture')
	duplicateLayoutCaptureRef?: ElementRef<HTMLElement>;
	readonly activeMissingPreferredActions: BeneficiaryReconciliationApplyAction[] =
		['CREATE_BENEFICIARY', 'REACTIVATE_BENEFICIARY'];
	private readonly congenerMissingFields = new Set<string>([
		'carteirinhaCongenere',
		'planoCongenere',
	]);
	private readonly conjugatedMissingFields = new Set<string>([
		'carteirinhaConjugado',
		'planoConjugado',
	]);
	private readonly normalDentalMissingFields = new Set<string>([
		'beneficioOdonto',
		'carteirinhaOdonto',
		'dentalBenefit',
		'dentalCardNumber',
		'dentalPlan',
		'dentalPlanName',
		'hasDentalBenefit',
		'planoOdonto',
	]);
	private readonly normalHealthMissingFields = new Set<string>([
		'beneficioSaude',
		'cardNumber',
		'carteirinhaSaude',
		'hasHealthBenefit',
		'healthBenefit',
		'healthCardNumber',
		'healthPlan',
		'healthPlanName',
		'planName',
		'planoSaude',
	]);

	private readonly actionOptionLabelSeparator = ' - ';
	errorMessage = '';
	form: UntypedFormGroup;
	isDownloadingDuplicateLayout = false;
	readonly kinshipOptions = Object.values(TypeKinship).map((value) => ({
		label: this.toReadableLabel(value),
		value,
	}));
	readonly resolutionOptions: {
		description: string;
		label: string;
		value: BeneficiaryReconciliationApplyAction;
	}[];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: InlineResolutionDialogData,
		private readonly dialogRef: MatDialogRef<BeneficiaryReconciliationInlineResolutionModalComponent>,
		private readonly fb: UntypedFormBuilder,
	) {
		const externalValue = (data.item.externalValue ?? {}) as Record<
			string,
			unknown
		>;
		const suggestedTargetValue = this.suggestedTargetValue;
		const candidateMatches = this.candidateMatches;
		const defaultAction =
			data.pendingChange?.action ??
			this.suggestedAction ??
			this.getActionOptions()[0] ??
			null;
		const defaultCandidate =
			data.pendingChange?.targetValue.beneficiaryId ??
			suggestedTargetValue.beneficiaryId ??
			data.item.beneficiaryId ??
			'';
		const defaultCandidateIndex =
			defaultCandidate && candidateMatches.length > 0
				? candidateMatches.findIndex(
						(candidate) => candidate.beneficiaryId === defaultCandidate,
					)
				: -1;
		const initialTypeKinship =
			data.pendingChange?.targetValue.typeKinship ??
			this.parseTypeKinship(externalValue.suggestedTypeKinship) ??
			this.parseTypeKinship(externalValue.parentesco) ??
			(this.getStringValue(externalValue.titularity) === 'T'
				? TypeKinship.TITULAR
				: null);

		this.resolutionOptions = this.getActionOptions().map((option) => ({
			description: this.getActionDescription(option),
			label: this.getActionLabel(option),
			value: option,
		}));
		this.form = this.fb.group({
			action: [defaultAction],
			birthDay: [data.pendingChange?.targetValue.birthDay ?? ''],
			email: [data.pendingChange?.targetValue.email ?? ''],
			holderCpf: [
				data.pendingChange?.targetValue.holderCpf ??
					this.getStringValue(externalValue.holderCpf),
			],
			name: [
				data.pendingChange?.targetValue.name ??
					data.item.beneficiaryName ??
					this.getStringValue(externalValue.beneficiaryName),
			],
			selectedBeneficiaryId: [defaultCandidate],
			selectedCandidateIndex: [
				defaultCandidateIndex >= 0 ? defaultCandidateIndex : null,
			],
			selectedDuplicateRowIndex: [
				this.resolveDefaultDuplicateRowIndex(data.pendingChange?.targetValue),
			],
			typeKinship: [initialTypeKinship],
		});
	}

	get action(): BeneficiaryReconciliationApplyAction {
		if (!this.isDuplicateInFile && !this.shouldShowActionChoices) {
			const primaryAction = this.primaryVisibleActionOption?.value;

			if (primaryAction) {
				return primaryAction;
			}
		}

		return (
			this.parseApplyAction(this.form.get('action')?.value) ?? 'IGNORE_ITEM'
		);
	}

	get canSave(): boolean {
		const selectedAction = this.parseApplyAction(
			this.form.get('action')?.value,
		);

		if (!selectedAction) {
			return false;
		}

		if (this.isDuplicateInFile) {
			const selectedRowIndexRaw = this.form.get(
				'selectedDuplicateRowIndex',
			)?.value;
			const selectedRowIndex = Number(selectedRowIndexRaw);

			return (
				selectedRowIndexRaw !== null &&
				selectedRowIndexRaw !== undefined &&
				selectedRowIndexRaw !== '' &&
				Number.isInteger(selectedRowIndex) &&
				selectedRowIndex >= 0 &&
				Boolean(this.duplicateRows[selectedRowIndex])
			);
		}

		if (this.shouldShowCandidateSelector) {
			return Boolean(
				typeof this.form.get('selectedCandidateIndex')?.value === 'number',
			);
		}

		return true;
	}

	get candidateMatches(): BeneficiaryReconciliationCandidateMatch[] {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;

		return Array.isArray(context.candidateMatches)
			? (context.candidateMatches as BeneficiaryReconciliationCandidateMatch[])
			: [];
	}

	get categoryDescription(): string {
		return this.getFieldDescription(this.data.item.field);
	}

	get categoryLabel(): string {
		return this.getFieldTitle(this.data.item.field);
	}

	get comparisonSummary(): { spreadsheet: string; system: string } {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const summary =
			context.comparisonSummary && typeof context.comparisonSummary === 'object'
				? (context.comparisonSummary as Record<string, unknown>)
				: {};

		return {
			spreadsheet: this.getStringValue(summary.spreadsheet),
			system: this.getStringValue(summary.system),
		};
	}

	get currentOrganizationLabel(): string {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;

		return (
			this.getStringValue(context.currentOrganizationName) ||
			this.data.item.organizationName ||
			'-'
		);
	}

	get currentSystemValue(): Record<string, unknown> | null {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const currentValue = context.currentValue;

		return currentValue && typeof currentValue === 'object'
			? (currentValue as Record<string, unknown>)
			: this.data.item.dbValue && typeof this.data.item.dbValue === 'object'
				? (this.data.item.dbValue as Record<string, unknown>)
				: null;
	}

	get dialogDescription(): string {
		if (this.isDuplicateInFile) {
			return '';
		}

		if (this.isOrganizationMismatch) {
			return 'A unidade atual na plataforma é diferente da unidade indicada na base do cliente.';
		}

		if (this.isActiveMissingCreateCase) {
			return 'Existe na base do cliente, mas ainda não está cadastrado na plataforma.';
		}

		if (this.isInactiveMissingInFile) {
			return 'Está ativo na plataforma, mas não apareceu na base do cliente enviada.';
		}

		return `Escolha como tratar ${
			this.data.item.beneficiaryName || 'este beneficiário'
		} e deixe a alteração pronta para o lote.`;
	}

	get dialogTitle(): string {
		if (this.isOrganizationMismatch) {
			return 'Divergência de CNPJ';
		}

		if (this.isActiveMissingCreateCase) {
			return 'Criar beneficiário';
		}

		if (this.isInactiveMissingInFile) {
			return 'Inativar beneficiário';
		}

		if (this.isDuplicateInFile) {
			return 'Duplicidade na base do cliente';
		}

		return 'Resolver divergência';
	}

	get duplicateDivergenceSummary(): string {
		const differences = [
			this.hasDifferentDuplicateValues('cardNumber')
				? 'carteirinhas diferentes'
				: '',
			this.hasDifferentDuplicateValues('planName') ? 'planos diferentes' : '',
			this.hasDifferentDuplicateValues('organizationName')
				? 'unidades diferentes'
				: '',
			this.hasDifferentDuplicateValues('carrier')
				? 'operadoras diferentes'
				: '',
			this.hasDifferentDuplicateValues('titularity') ||
			this.hasDifferentDuplicateValues('holderCpf') ||
			this.hasDifferentDuplicateValues('parentesco')
				? 'titularidades diferentes'
				: '',
		].filter(Boolean);
		const differenceLabel =
			differences.length > 0
				? `com ${differences.join(', ')}`
				: 'mais de uma vez na base do cliente';

		return `${this.duplicateRows.length} linhas para o mesmo beneficiário ${differenceLabel}. Escolha qual registro deve ser mantido.`;
	}

	get duplicateRows(): DuplicateRow[] {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const duplicates = Array.isArray(context.duplicates)
			? context.duplicates
			: [];

		return duplicates.filter((row): row is DuplicateRow =>
			Boolean(row && typeof row === 'object'),
		);
	}

	get duplicateSubjectCpf(): string {
		const subject = this.subjectBeneficiary;
		const cpf =
			this.getStringValue(subject.cpf) || this.data.item.beneficiaryCpf || '';

		return this.formatCpf(cpf);
	}

	get duplicateSubjectName(): string {
		const subject = this.subjectBeneficiary;

		return (
			this.getStringValue(subject.name) || this.data.item.beneficiaryName || '-'
		);
	}

	get creationDataFields(): CreationDataField[] {
		const externalValue = this.externalSnapshot;
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const typeKinship = this.getDisplayValue(
			externalValue,
			'suggestedTypeKinship',
			'parentesco',
			'titularity',
		);
		const normalizedTypeKinship = typeKinship.toUpperCase();
		const shouldShowHolderCpf =
			normalizedTypeKinship !== '-' &&
			normalizedTypeKinship !== 'T' &&
			normalizedTypeKinship !== 'TITULAR';
		const healthBenefit = this.formatNullableBenefitStatus(
			this.healthBenefitStatusFromSpreadsheet,
		);
		const dentalBenefit = this.formatNullableBenefitStatus(
			this.parseBenefitStatus(
				this.pickFirstKnownValue(externalValue, [
					'dentalBenefit',
					'beneficioOdonto',
					'hasDentalBenefit',
				]),
			),
		);
		const fields: CreationDataField[] = [
			this.createDataField(
				'Nome',
				this.getStringValue(externalValue.beneficiaryName) ||
					this.data.item.beneficiaryName,
				'essential',
			),
			this.createDataField(
				'CPF',
				this.formatCpf(
					this.getStringValue(externalValue.beneficiaryCpf) ||
						this.data.item.beneficiaryCpf,
				),
				'essential',
			),
			this.createDataField(
				'Data de nascimento',
				this.getDisplayValue(externalValue, 'birthDay', 'dataNascimento'),
				'essential',
			),
			this.createDataField('Titularidade', typeKinship, 'essential'),
			this.createDataField(
				'Unidade',
				this.expectedOrganizationLabel,
				'essential',
			),
			this.createDataField(
				'CNPJ',
				this.getStringValue(context.expectedOrganizationCnpj) ||
					this.getStringValue(externalValue.organizationCnpj),
				'essential',
			),
			...(shouldShowHolderCpf
				? [
						this.createDataField(
							'CPF do titular',
							this.formatCpf(this.getStringValue(externalValue.holderCpf)),
							'essential',
						),
					]
				: []),
			this.createDataField(
				'Telefone',
				this.getDisplayValue(externalValue, 'phone', 'telefone'),
				'complementary',
			),
			this.createDataField(
				'Gênero',
				this.getDisplayValue(externalValue, 'gender', 'sexo'),
				'complementary',
			),
			this.createDataField('Benefício saúde', healthBenefit, 'complementary'),
			this.createDataField(
				'Plano saúde',
				this.getDisplayValue(
					externalValue,
					'healthPlanName',
					'planName',
					'planoSaude',
				),
				'complementary',
			),
			this.createDataField(
				'Carteirinha saúde',
				this.getDisplayValue(
					externalValue,
					'healthCardNumber',
					'cardNumber',
					'carteirinhaSaude',
				),
				'complementary',
			),
			this.createDataField('Benefício odonto', dentalBenefit, 'complementary'),
			this.createDataField(
				'Plano odonto',
				this.getDisplayValue(externalValue, 'dentalPlanName', 'planoOdonto'),
				'complementary',
			),
			this.createDataField(
				'Carteirinha odonto',
				this.getDisplayValue(
					externalValue,
					'dentalCardNumber',
					'carteirinhaOdonto',
				),
				'complementary',
			),
		];

		return fields
			.map((field) => this.applyCreationMissingState(field))
			.filter((field) => {
				return (
					field.group === 'essential' || field.value !== '-' || field.isMissing
				);
			});
	}

	get creationMissingFieldsForDisplay(): string[] {
		return Array.from(
			new Set([
				...this.missingEssentialFieldsForDisplay,
				...this.missingImportantFieldsForDisplay,
			]),
		);
	}

	get creationComplementaryMissingFieldsForDisplay(): string[] {
		const essentialFields = new Set(this.missingEssentialFieldsForDisplay);

		return this.creationMissingFieldsForDisplay.filter(
			(field) => !essentialFields.has(field),
		);
	}

	get missingEssentialFieldsSentence(): string {
		return this.missingEssentialFieldsForDisplay.join(', ');
	}

	get expectedOrganizationLabel(): string {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const externalValue = (this.data.item.externalValue ?? {}) as Record<
			string,
			unknown
		>;

		return (
			this.getStringValue(context.expectedOrganizationName) ||
			this.getStringValue(externalValue.organizationName) ||
			'-'
		);
	}

	get externalCarrierLabel(): string {
		const carrier = this.getDisplayValue(this.externalSnapshot, 'carrier');

		return carrier !== '-' ? carrier : this.data.item.carrier || '-';
	}

	get externalSnapshot(): Record<string, unknown> {
		return ((this.data.item.context as Record<string, unknown> | null)
			?.externalValue ??
			this.data.item.externalValue ??
			{}) as Record<string, unknown>;
	}

	get hasInactiveCandidate(): boolean {
		return this.candidateMatches.some(
			(candidate) => !candidate.active || candidate.isDeleted,
		);
	}

	get hasPendingChange(): boolean {
		return Boolean(this.data.pendingChange);
	}

	get canDefinitivelyIgnore(): boolean {
		return this.isActiveMissingInDatabase || this.isDuplicateInFile;
	}

	get hasSystemRecord(): boolean {
		return Boolean(this.currentSystemValue) || this.candidateMatches.length > 0;
	}

	get healthBenefitStatusFromSpreadsheet(): boolean | null {
		return this.parseBenefitStatus(
			this.pickFirstKnownValue(this.externalSnapshot, [
				'healthBenefit',
				'beneficioSaude',
				'hasHealthBenefit',
			]),
		);
	}

	get inactivePlatformFields(): { label: string; value: string }[] {
		return [
			{
				label: 'Beneficiário',
				value: this.getSystemNameLabel(),
			},
			{
				label: 'CPF',
				value: this.formatCpf(this.data.item.beneficiaryCpf),
			},
			{
				label: 'Operadora',
				value: this.data.item.carrier || '-',
			},
			{
				label: 'Unidade',
				value:
					this.data.item.organizationName ||
					this.getDisplayValue(this.currentSystemValue, 'organizationName'),
			},
			{
				label: 'Plano',
				value: this.getDisplayValue(this.currentSystemValue, 'planName'),
			},
			{
				label: 'Carteirinha',
				value: this.getDisplayValue(this.currentSystemValue, 'cardNumber'),
			},
			{
				label: 'Titularidade',
				value: this.getDisplayValue(
					this.currentSystemValue,
					'titularity',
					'parentesco',
				),
			},
		];
	}

	get isActiveMissingCreateCase(): boolean {
		return this.isActiveMissingInDatabase && this.candidateMatches.length === 0;
	}

	get isActiveMissingInDatabase(): boolean {
		return this.data.item.field === 'ACTIVE_MISSING_IN_DB';
	}

	get isActiveMissingReactivationCase(): boolean {
		return this.isActiveMissingInDatabase && this.candidateMatches.length > 0;
	}

	get isComparisonSummaryLayout(): boolean {
		return (
			this.isActiveMissingInDatabase ||
			this.isInactiveMissingInFile ||
			this.isOrganizationMismatch ||
			this.isDuplicateInFile
		);
	}

	get isDuplicateInFile(): boolean {
		return this.data.item.field === 'DUPLICATE_IN_FILE';
	}

	get isInactiveMissingInFile(): boolean {
		return this.data.item.field === 'INACTIVE_MISSING_IN_FILE';
	}

	get isOrganizationMismatch(): boolean {
		return this.data.item.field === 'ORGANIZATION_MISMATCH';
	}

	get missingEssentialFieldsForDisplay(): string[] {
		if (
			this.isDuplicateInFile ||
			this.isOrganizationMismatch ||
			this.isInactiveMissingInFile
		) {
			return [];
		}

		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const rawValue = context.missingRequiredFields;

		return this.parseMissingFieldList(rawValue).map((field) =>
			this.getEssentialMissingFieldLabel(field),
		);
	}

	get missingImportantFieldsForDisplay(): string[] {
		if (
			this.isDuplicateInFile ||
			this.isOrganizationMismatch ||
			this.isInactiveMissingInFile
		) {
			return [];
		}

		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const rawValue = context.missingFields;
		const mapped = this.parseMissingFieldList(rawValue)
			.filter((field) =>
				this.shouldDisplayImportantMissingField(field, context),
			)
			.map((field) => this.getImportantMissingFieldLabel(field));

		if (
			this.shouldDisplayMissingHealthBenefitFromSpreadsheet(context) &&
			this.healthBenefitStatusFromSpreadsheet === null
		) {
			mapped.push('Benefício saúde (dado não informado)');
		}

		return Array.from(new Set(mapped));
	}

	get resolutionNote(): string {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;

		return this.getStringValue(context.resolutionNote);
	}

	get selectedActionDescription(): string {
		return (
			this.resolutionOptions.find((option) => option.value === this.action)
				?.description ?? ''
		);
	}

	get selectedActionLabel(): string {
		return (
			this.resolutionOptions.find((option) => option.value === this.action)
				?.label ?? this.getActionLabel(this.action)
		);
	}

	get primaryVisibleActionOption():
		| {
				description: string;
				label: string;
				value: BeneficiaryReconciliationApplyAction;
		  }
		| undefined {
		return this.visibleActionOptions[0];
	}

	get shouldShowActionChoices(): boolean {
		return this.visibleActionOptions.length > 1;
	}

	get shouldHideHealthCardByBenefitValue(): boolean {
		return this.healthBenefitStatusFromSpreadsheet === false;
	}

	get shouldHighlightSpreadsheetSummary(): boolean {
		return this.isInactiveMissingInFile;
	}

	get shouldHighlightSystemSummary(): boolean {
		return this.isActiveMissingInDatabase || this.isInactiveMissingInFile;
	}

	get shouldShowActiveMissingSystemDetails(): boolean {
		return !this.isActiveMissingInDatabase;
	}

	get shouldShowBirthDay(): boolean {
		return this.shouldShowCreateBeneficiaryFields;
	}

	get shouldShowCandidateSection(): boolean {
		return this.shouldShowCandidateSelector;
	}

	get shouldShowCandidateSelector(): boolean {
		return (
			this.data.item.field === 'ACTIVE_MISSING_IN_DB' &&
			this.action === 'REACTIVATE_BENEFICIARY'
		);
	}

	get shouldShowHolderCpf(): boolean {
		const typeKinship = this.form.get('typeKinship')
			?.value as TypeKinship | null;

		if (!typeKinship) {
			return false;
		}

		return typeKinship !== TypeKinship.TITULAR;
	}

	get shouldShowName(): boolean {
		return this.shouldShowCreateBeneficiaryFields;
	}

	get shouldShowTitularityFields(): boolean {
		return this.shouldShowCreateBeneficiaryFields;
	}

	get spreadsheetAllFields(): { label: string; value: string }[] {
		const source = this.externalSnapshot;
		const hiddenKeys = new Set([
			'foundInSpreadsheet',
			'message',
			'suggestedTypeKinship',
		]);

		return Object.entries(source)
			.filter(([key, value]) => {
				if (hiddenKeys.has(key)) {
					return false;
				}

				if (
					this.shouldHideHealthCardByBenefitValue &&
					this.isHealthCardField(key)
				) {
					return false;
				}

				if (value === null || value === undefined) {
					return false;
				}

				if (typeof value === 'string') {
					return value.trim() !== '';
				}

				return typeof value !== 'object';
			})
			.map(([key, value]) => ({
				label: this.formatFieldLabel(key),
				value: this.formatFieldValueByKey(key, value),
			}));
	}

	get spreadsheetSectionDescription(): string {
		if (this.isInactiveMissingInFile) {
			return this.getDisplayValue(this.externalSnapshot, 'message') !== '-'
				? this.getDisplayValue(this.externalSnapshot, 'message')
				: 'Beneficiário não encontrado na base do cliente.';
		}

		if (this.isOrganizationMismatch) {
			return 'Unidade esperada de acordo com a base do cliente enviada.';
		}

		return 'Dados capturados da base do cliente enviada.';
	}

	get subjectBeneficiary(): Record<string, unknown> {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const subject = context.subjectBeneficiary;

		return subject && typeof subject === 'object'
			? (subject as Record<string, unknown>)
			: {};
	}

	get suggestedAction(): BeneficiaryReconciliationApplyAction | null {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const value = context.suggestedAction ?? this.data.item.suggestedAction;

		return this.parseApplyAction(value);
	}

	get suggestedActionDescription(): string {
		if (!this.suggestedAction) {
			return '';
		}

		return this.getActionLabel(this.suggestedAction);
	}

	get suggestedTargetValue(): BeneficiaryReconciliationApplyTargetValue {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const value = context.suggestedTargetValue;

		return value && typeof value === 'object'
			? (value as BeneficiaryReconciliationApplyTargetValue)
			: {};
	}

	get summarySpreadsheetStatusLabel(): string {
		const comparisonSummary = this.comparisonSummary;

		if (comparisonSummary.spreadsheet) {
			return comparisonSummary.spreadsheet;
		}

		if (this.isInactiveMissingInFile) {
			return 'Não encontrado';
		}

		if (this.isOrganizationMismatch) {
			return this.expectedOrganizationLabel;
		}

		return 'Ativo';
	}

	get summarySystemStatusLabel(): string {
		const comparisonSummary = this.comparisonSummary;

		if (comparisonSummary.system) {
			return comparisonSummary.system;
		}

		if (this.isOrganizationMismatch) {
			return this.currentOrganizationLabel;
		}

		if (!this.hasSystemRecord) {
			return 'Não encontrado';
		}

		if (this.isInactiveMissingInFile) {
			return 'Ativo';
		}

		if (this.hasInactiveCandidate) {
			return 'Inativo';
		}

		return 'Encontrado';
	}

	get systemRecordStatusLabel(): string {
		if (this.isOrganizationMismatch) {
			return 'Ativo';
		}

		const currentValue = this.currentSystemValue;

		if (currentValue) {
			const activeValue = currentValue.active;

			if (typeof activeValue === 'boolean') {
				return activeValue ? 'Ativo' : 'Inativo';
			}
		}

		return this.hasInactiveCandidate ? 'Inativo' : 'Ativo';
	}

	get systemSectionDescription(): string {
		if (this.isInactiveMissingInFile) {
			return 'Confirme o cadastro que será inativado se a alteração for salva na fila.';
		}

		if (this.isOrganizationMismatch) {
			return 'Unidade em que o beneficiário está vinculado hoje na plataforma.';
		}

		if (!this.hasSystemRecord) {
			return 'Beneficiário não encontrado na plataforma.';
		}

		return 'Veja abaixo o que já existe na plataforma para este CPF.';
	}

	get transferDescription(): string {
		return `O cadastro será movido de ${this.currentOrganizationLabel} para ${this.expectedOrganizationLabel}.`;
	}

	get visibleActionOptions(): {
		description: string;
		label: string;
		value: BeneficiaryReconciliationApplyAction;
	}[] {
		if (this.isInactiveMissingInFile || this.isOrganizationMismatch) {
			return this.resolutionOptions;
		}

		if (!this.isActiveMissingInDatabase) {
			return this.resolutionOptions.filter(
				(option) => option.value !== 'IGNORE_ITEM',
			);
		}

		const byValue = new Map(
			this.resolutionOptions.map((option) => [option.value, option]),
		);
		const preferred = this.activeMissingPreferredActions
			.map((action) => byValue.get(action))
			.filter(
				(
					option,
				): option is {
					description: string;
					label: string;
					value: BeneficiaryReconciliationApplyAction;
				} => Boolean(option),
			);

		return (preferred.length > 0 ? preferred : this.resolutionOptions).filter(
			(option) => option.value !== 'IGNORE_ITEM',
		);
	}

	getActionOptionTitle(label: string): string {
		const separatorIndex = label.indexOf(this.actionOptionLabelSeparator);

		if (separatorIndex < 0) {
			return label;
		}

		return label.slice(0, separatorIndex);
	}

	getActionOptionDetail(label: string): string {
		const separatorIndex = label.indexOf(this.actionOptionLabelSeparator);

		if (separatorIndex < 0) {
			return '';
		}

		return label.slice(separatorIndex + this.actionOptionLabelSeparator.length);
	}

	formatCpf(value: string | null | undefined): string {
		const digits = this.getStringValue(value).replace(/\D/g, '');

		if (digits.length !== 11) {
			return this.getStringValue(value);
		}

		return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
	}

	getCandidateDetails(
		candidate: BeneficiaryReconciliationCandidateMatch,
	): { label: string; value: string }[] {
		return [
			{
				label: 'CPF',
				value: this.formatCpf(candidate.beneficiaryCpf),
			},
			{
				label: 'Plano',
				value: candidate.planName || '-',
			},
			{
				label: 'Operadora',
				value: candidate.carrier || '-',
			},
			{
				label: 'CNPJ',
				value: candidate.organizationCnpj || '-',
			},
			{
				label: 'Unidade',
				value: candidate.organizationName || '-',
			},
			{
				label: 'Titularidade',
				value: candidate.typeKinship || '-',
			},
		];
	}

	getCandidateHint(candidate: BeneficiaryReconciliationCandidateMatch): string {
		if (!candidate.active || candidate.isDeleted) {
			return 'Cadastro inativo';
		}

		if (!candidate.isSameMatrix) {
			return 'Ativo em outra unidade';
		}

		return 'Cadastro já encontrado na plataforma';
	}

	getCandidateMeta(
		candidate: BeneficiaryReconciliationCandidateMatch,
	): string[] {
		return [
			candidate.organizationName || '',
			candidate.planName || '',
			candidate.carrier || '',
		].filter(Boolean);
	}

	getDisplayValue(
		source: Record<string, unknown> | null,
		...keys: string[]
	): string {
		if (!source) {
			return '-';
		}

		for (const key of keys) {
			const rawValue = source[key];
			const stringValue = this.getStringValue(rawValue);

			if (stringValue) {
				return stringValue;
			}
		}

		return '-';
	}

	getDuplicateRowCompactDetails(
		row: DuplicateRow,
	): { isDifferent: boolean; label: string; value: string }[] {
		return [
			{
				isDifferent: this.hasDifferentDuplicateValues('cardNumber'),
				label: 'Carteirinha',
				value: row.cardNumber || '-',
			},
			{
				isDifferent: this.hasDifferentDuplicateValues('planName'),
				label: 'Plano',
				value: row.planName || '-',
			},
			{
				isDifferent: this.hasDifferentDuplicateValues('carrier'),
				label: 'Operadora',
				value: row.carrier || '-',
			},
			{
				isDifferent: this.hasDifferentDuplicateValues('organizationName'),
				label: 'Unidade',
				value: row.organizationName || '-',
			},
		];
	}

	getDuplicateRowDetails(row: DuplicateRow): string[] {
		return [
			row.carrier ? `Operadora: ${row.carrier}` : '',
			row.planName ? `Plano: ${row.planName}` : '',
			row.cardNumber ? `Carteirinha: ${row.cardNumber}` : '',
			row.organizationName ? `Unidade: ${row.organizationName}` : '',
			row.titularity ? `Titularidade: ${row.titularity}` : '',
			row.holderCpf ? `Titular: ${this.formatCpf(row.holderCpf)}` : '',
			row.parentesco ? `Parentesco: ${row.parentesco}` : '',
		].filter(Boolean);
	}

	getDuplicateRowSource(row: DuplicateRow): string {
		const parts = [
			row.sourceFileName ?? '',
			row.sheetName ? `Aba ${row.sheetName}` : '',
		].filter(Boolean);

		return parts.join(' • ') || '-';
	}

	getSystemNameLabel(): string {
		return this.getDisplayValue(
			this.currentSystemValue,
			'beneficiaryName',
			'name',
		) !== '-'
			? this.getDisplayValue(this.currentSystemValue, 'beneficiaryName', 'name')
			: this.data.item.beneficiaryName || '-';
	}

	getSystemOrganizationLabel(): string {
		const systemOrganization = this.getDisplayValue(
			this.currentSystemValue,
			'organizationName',
			'currentOrganizationName',
		);

		return systemOrganization !== '-'
			? systemOrganization
			: this.currentOrganizationLabel;
	}

	isSuggestedOption(action: BeneficiaryReconciliationApplyAction): boolean {
		return this.suggestedAction === action;
	}

	onClose(): void {
		this.dialogRef.close();
	}

	onDownloadDuplicateLayout(): void {
		if (!this.isDuplicateInFile || this.isDownloadingDuplicateLayout) {
			return;
		}

		const captureElement = this.duplicateLayoutCaptureRef?.nativeElement;

		if (!captureElement) {
			this.errorMessage =
				'Não foi possível preparar o layout para download neste item.';

			return;
		}

		this.errorMessage = '';
		this.isDownloadingDuplicateLayout = true;
		void this.downloadDuplicateLayout(captureElement).finally(() => {
			this.isDownloadingDuplicateLayout = false;
		});
	}

	onRemove(): void {
		this.dialogRef.close({ removePending: true });
	}

	onIgnore(): void {
		this.dialogRef.close({
			action: 'IGNORE_ITEM',
			onHold: true,
			targetValue: {},
		} satisfies BeneficiaryReconciliationInlineResolutionResult);
	}

	onDefinitiveIgnore(): void {
		this.dialogRef.close({
			action: 'IGNORE_ITEM',
			definitiveIgnore: true,
			targetValue: {},
		} satisfies BeneficiaryReconciliationInlineResolutionResult);
	}

	onApplyOnly(): void {
		this.onSave(true);
	}

	onSave(applyOnly = false): void {
		if (this.isDuplicateInFile) {
			const selectedRowIndexRaw = this.form.get(
				'selectedDuplicateRowIndex',
			)?.value;

			if (
				selectedRowIndexRaw === null ||
				selectedRowIndexRaw === undefined ||
				selectedRowIndexRaw === ''
			) {
				this.dialogRef.close({
					action: 'IGNORE_ITEM',
					onHold: true,
					targetValue: {},
				} satisfies BeneficiaryReconciliationInlineResolutionResult);

				return;
			}

			const selectedRowIndex = Number(selectedRowIndexRaw);
			const selectedRow = this.duplicateRows[selectedRowIndex];

			if (
				!Number.isInteger(selectedRowIndex) ||
				selectedRowIndex < 0 ||
				!selectedRow
			) {
				this.dialogRef.close({
					action: 'IGNORE_ITEM',
					onHold: true,
					targetValue: {},
				} satisfies BeneficiaryReconciliationInlineResolutionResult);

				return;
			}

			this.dialogRef.close({
				action: 'SELECT_DUPLICATE_ROW',
				applyOnly: applyOnly || undefined,
				targetValue: {
					rowNumber: selectedRow.rowNumber ?? undefined,
					sheetName: selectedRow.sheetName ?? undefined,
					sourceFileName: selectedRow.sourceFileName ?? undefined,
					uid: selectedRow.uid ?? undefined,
				},
			} satisfies BeneficiaryReconciliationInlineResolutionResult);

			return;
		}

		const action = this.getSubmissionAction();
		const typeKinship = this.form.get('typeKinship')
			?.value as TypeKinship | null;
		const holderCpf = this.sanitizeCpf(this.form.get('holderCpf')?.value);
		const selectedBeneficiaryId = this.getStringValue(
			this.form.get('selectedBeneficiaryId')?.value,
		);

		if (!action) {
			this.errorMessage = 'Selecione a ação que deve ser aplicada.';

			return;
		}

		if (this.shouldShowCandidateSelector && !selectedBeneficiaryId) {
			this.errorMessage =
				'Selecione um cadastro já encontrado na plataforma para continuar.';

			return;
		}

		const createTargetValueFromSnapshot =
			action === 'CREATE_BENEFICIARY'
				? this.buildCreateTargetValueFromSnapshot()
				: null;

		if (action === 'CREATE_BENEFICIARY' && !createTargetValueFromSnapshot) {
			this.errorMessage =
				'Dados da base do cliente incompletos para criar automaticamente.';

			return;
		}

		const targetValue: BeneficiaryReconciliationApplyTargetValue = {};

		if (this.shouldShowCandidateSelector) {
			targetValue.beneficiaryId = selectedBeneficiaryId;
		}

		if (this.shouldShowName) {
			targetValue.name = this.getStringValue(this.form.get('name')?.value);
			targetValue.birthDay = this.getStringValue(
				this.form.get('birthDay')?.value,
			);
			targetValue.email =
				this.getStringValue(this.form.get('email')?.value) || undefined;
		}

		if (action === 'CREATE_BENEFICIARY' && createTargetValueFromSnapshot) {
			targetValue.birthDay = createTargetValueFromSnapshot.birthDay;
			targetValue.holderCpf = createTargetValueFromSnapshot.holderCpf;
			targetValue.name = createTargetValueFromSnapshot.name;
			targetValue.organizationId = createTargetValueFromSnapshot.organizationId;
			targetValue.typeKinship = createTargetValueFromSnapshot.typeKinship;
		}

		if (this.shouldShowTitularityFields && typeKinship) {
			targetValue.typeKinship = typeKinship;
			targetValue.holderCpf =
				typeKinship === TypeKinship.TITULAR
					? undefined
					: holderCpf || undefined;
		}

		this.dialogRef.close({
			action,
			applyOnly: applyOnly || undefined,
			onHold: this.isOnHoldSubmission(action),
			targetValue,
		} satisfies BeneficiaryReconciliationInlineResolutionResult);
	}

	onSelectAction(action: BeneficiaryReconciliationApplyAction): void {
		this.form.patchValue({ action });
		this.errorMessage = '';
	}

	selectCandidate(
		candidate: BeneficiaryReconciliationCandidateMatch,
		index?: number,
	): void {
		const resolvedIndex =
			typeof index === 'number'
				? index
				: this.candidateMatches.findIndex(
						(current) => current.beneficiaryId === candidate.beneficiaryId,
					);

		this.form.patchValue({
			selectedBeneficiaryId: candidate.beneficiaryId,
			selectedCandidateIndex: resolvedIndex >= 0 ? resolvedIndex : null,
		});
	}

	selectDuplicateIgnore(): void {
		this.form.patchValue({
			selectedDuplicateRowIndex: null,
		});
	}

	selectDuplicateRow(index: number): void {
		this.form.patchValue({
			selectedDuplicateRowIndex: index,
		});
	}

	trackByCandidate(
		index: number,
		candidate: BeneficiaryReconciliationCandidateMatch,
	): string {
		return candidate.beneficiaryId;
	}

	private buildCreateTargetValueFromSnapshot(): BeneficiaryReconciliationApplyTargetValue | null {
		const external = this.externalSnapshot;
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const name =
			this.getStringValue(external.beneficiaryName) ||
			this.data.item.beneficiaryName ||
			'';
		const birthDay = this.getStringValue(external.birthDay);
		const organizationId =
			this.getStringValue(context.expectedOrganizationId) ||
			this.getStringValue(external.organizationId) ||
			undefined;
		const inferredTypeKinship =
			this.parseTypeKinship(external.suggestedTypeKinship) ??
			this.parseTypeKinship(external.parentesco) ??
			(this.getStringValue(external.titularity) === 'T'
				? TypeKinship.TITULAR
				: null);
		const normalizedHolderCpf = this.sanitizeCpf(external.holderCpf);

		if (!name || !birthDay || !inferredTypeKinship) {
			return null;
		}

		if (
			inferredTypeKinship !== TypeKinship.TITULAR &&
			normalizedHolderCpf.length !== 11
		) {
			return null;
		}

		return {
			birthDay,
			holderCpf:
				inferredTypeKinship === TypeKinship.TITULAR
					? undefined
					: normalizedHolderCpf,
			name,
			organizationId,
			typeKinship: inferredTypeKinship,
		};
	}

	private buildDuplicateLayoutFileName(): string {
		const cpfDigits = this.sanitizeCpf(this.data.item.beneficiaryCpf);
		const safeCpf = cpfDigits || 'beneficiario';
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

		return `duplicidade-${safeCpf}-${timestamp}.png`;
	}

	private createDataField(
		label: string,
		rawValue: string | null | undefined,
		group: CreationDataField['group'],
	): CreationDataField {
		const value = this.getStringValue(rawValue);
		const normalizedValue = value === 'Dado não informado' ? '' : value;

		return {
			group,
			isMissing: !normalizedValue,
			label,
			value: normalizedValue || '-',
		};
	}

	private applyCreationMissingState(
		field: CreationDataField,
	): CreationDataField {
		if (!this.isCreationFieldListedAsMissing(field.label)) {
			return field;
		}

		return {
			...field,
			isMissing: true,
			value: '-',
		};
	}

	private async downloadDuplicateLayout(element: HTMLElement): Promise<void> {
		try {
			const dataUrl = this.renderDuplicateRowsAsPng(element);

			this.triggerDownload(dataUrl, this.buildDuplicateLayoutFileName());
		} catch {
			this.errorMessage =
				'Não foi possível gerar o layout agora. Tente novamente em instantes.';
		}
	}

	private formatFieldLabel(key: string): string {
		const labels: Record<string, string> = {
			beneficiaryCpf: 'CPF',
			beneficiaryName: 'Nome',
			birthDay: 'Data de nascimento',
			cardNumber: 'Carteirinha saúde',
			carrier: 'Operadora',
			carteirinhaCongenere: 'Carteirinha congênere',
			carteirinhaConjugado: 'Carteirinha conjugado',
			dentalBenefit: 'Benefício odontológico',
			dentalCardNumber: 'Carteirinha odonto',
			gender: 'Gênero',
			healthBenefit: 'Benefício saúde',
			healthCardNumber: 'Carteirinha saúde',
			holderCpf: 'CPF titular',
			holderName: 'Titular',
			organizationCnpj: 'CNPJ',
			organizationName: 'Unidade',
			parentesco: 'Parentesco',
			phone: 'Telefone',
			planName: 'Plano',
			planoCongenere: 'Plano congênere',
			planoConjugado: 'Plano conjugado',
			titularity: 'Titularidade',
		};

		if (labels[key]) {
			return labels[key];
		}

		return key
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			.replace(/[_-]+/g, ' ')
			.replace(/\s+/g, ' ')
			.trim()
			.replace(/^./, (letter) => letter.toUpperCase());
	}

	private formatFieldValue(value: unknown): string {
		if (typeof value === 'boolean') {
			return value ? 'Sim' : 'Não';
		}

		if (typeof value === 'number') {
			return String(value);
		}

		return this.getStringValue(value) || '-';
	}

	private formatFieldValueByKey(key: string, value: unknown): string {
		if (this.isBenefitField(key)) {
			const benefitStatus = this.parseBenefitStatus(value);

			if (benefitStatus === true) {
				return 'Sim';
			}

			if (benefitStatus === false) {
				return 'Não';
			}

			return 'Dado não informado';
		}

		return this.formatFieldValue(value);
	}

	private formatNullableBenefitStatus(value: boolean | null): string {
		if (value === true) {
			return 'Sim';
		}

		return '';
	}

	private getActionDescription(
		action: BeneficiaryReconciliationApplyAction,
	): string {
		if (
			this.data.item.field === 'DUPLICATE_IN_FILE' &&
			action === 'IGNORE_ITEM'
		) {
			return 'Coloca esta duplicidade em espera para revisar depois.';
		}

		switch (action) {
			case 'CREATE_BENEFICIARY':
				return 'Cria este beneficiário na plataforma usando os dados confirmados da base do cliente.';
			case 'IGNORE_ITEM':
				if (this.data.item.field === 'ACTIVE_MISSING_IN_DB') {
					return 'Coloca este caso em espera sem aplicar alterações e sem bloquear a confirmação final.';
				}

				if (this.data.item.field === 'ORGANIZATION_MISMATCH') {
					return `Mantém o beneficiário vinculado à unidade atual: ${this.currentOrganizationLabel}.`;
				}

				return 'Mantém o valor já existente na plataforma e encerra esta divergência.';
			case 'INACTIVATE_BENEFICIARY':
				return 'Inativa o cadastro que existe hoje na plataforma e não apareceu na base do cliente.';
			case 'LINK_EXISTING_BENEFICIARY':
				return 'Usa um cadastro já ativo que encontramos na plataforma para este CPF.';
			case 'REACTIVATE_BENEFICIARY':
				return 'Reativa um cadastro antigo já encontrado na plataforma antes de aplicar os dados da base do cliente.';
			case 'TRANSFER_ORGANIZATION':
				if (this.data.item.field === 'ORGANIZATION_MISMATCH') {
					return `Troca para a unidade indicada na base do cliente: ${this.expectedOrganizationLabel}.`;
				}

				return 'Move o beneficiário da sub atual para a sub esperada na base do cliente.';
			default:
				return 'Aplica a ação selecionada para resolver esta divergência.';
		}
	}

	private getActionLabel(action: BeneficiaryReconciliationApplyAction): string {
		if (
			this.data.item.field === 'DUPLICATE_IN_FILE' &&
			action === 'IGNORE_ITEM'
		) {
			return 'Colocar em espera';
		}

		switch (action) {
			case 'CREATE_BENEFICIARY':
				return 'Criar novo cadastro';
			case 'IGNORE_ITEM':
				if (this.data.item.field === 'ACTIVE_MISSING_IN_DB') {
					return 'Colocar em espera';
				}

				if (this.data.item.field === 'ORGANIZATION_MISMATCH') {
					return `Manter valor da plataforma - ${this.currentOrganizationLabel}`;
				}

				return 'Manter valor da plataforma';
			case 'INACTIVATE_BENEFICIARY':
				return 'Inativar cadastro';
			case 'LINK_EXISTING_BENEFICIARY':
				return 'Vincular existente';
			case 'REACTIVATE_BENEFICIARY':
				return 'Reativar beneficiário';
			case 'TRANSFER_ORGANIZATION':
				if (this.data.item.field === 'ORGANIZATION_MISMATCH') {
					return `Trocar para valor da base do cliente - ${this.expectedOrganizationLabel}`;
				}

				return 'Transferir de unidade';
			default:
				return action;
		}
	}

	private getActionOptions(): BeneficiaryReconciliationApplyAction[] {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const options = Array.isArray(context.resolutionOptions)
			? (context.resolutionOptions as BeneficiaryReconciliationApplyAction[])
			: [];

		if (options.length > 0) {
			if (this.isInactiveMissingInFile || this.isOrganizationMismatch) {
				return options.includes('IGNORE_ITEM')
					? options
					: [...options, 'IGNORE_ITEM'];
			}

			if (this.isActiveMissingInDatabase || this.isDuplicateInFile) {
				return options.filter((option) => option !== 'IGNORE_ITEM');
			}

			return options;
		}

		switch (this.data.item.field) {
			case 'ACTIVE_MISSING_IN_DB':
				return ['CREATE_BENEFICIARY'];
			case 'INACTIVE_MISSING_IN_FILE':
				return ['INACTIVATE_BENEFICIARY', 'IGNORE_ITEM'];
			case 'ORGANIZATION_MISMATCH':
				return ['TRANSFER_ORGANIZATION', 'IGNORE_ITEM'];
			case 'DUPLICATE_IN_FILE':
				return ['SELECT_DUPLICATE_ROW'];
			default:
				return ['IGNORE_ITEM'];
		}
	}

	private getSubmissionAction(): BeneficiaryReconciliationApplyAction | null {
		const selectedAction = this.parseApplyAction(
			this.form.get('action')?.value,
		);

		if (this.shouldShowActionChoices) {
			return selectedAction;
		}

		return this.primaryVisibleActionOption?.value ?? selectedAction;
	}

	private isOnHoldSubmission(
		action: BeneficiaryReconciliationApplyAction,
	): boolean {
		return (
			action === 'IGNORE_ITEM' &&
			(this.isActiveMissingInDatabase || this.isDuplicateInFile)
		);
	}

	private getDuplicateDefaultTargetValueFromContext():
		| BeneficiaryReconciliationApplyTargetValue
		| undefined {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const resolution =
			context.resolution && typeof context.resolution === 'object'
				? (context.resolution as Record<string, unknown>)
				: null;
		const resolutionAction = this.parseApplyAction(resolution?.action);
		const resolutionPayload =
			resolution?.payload && typeof resolution.payload === 'object'
				? (resolution.payload as BeneficiaryReconciliationApplyTargetValue)
				: undefined;

		if (resolutionAction === 'SELECT_DUPLICATE_ROW' && resolutionPayload) {
			return resolutionPayload;
		}

		const suggestedAction = this.parseApplyAction(
			context.suggestedAction ?? this.data.item.suggestedAction,
		);
		const suggestedTargetValue =
			context.suggestedTargetValue &&
			typeof context.suggestedTargetValue === 'object'
				? (context.suggestedTargetValue as BeneficiaryReconciliationApplyTargetValue)
				: undefined;

		if (suggestedAction === 'SELECT_DUPLICATE_ROW' && suggestedTargetValue) {
			return suggestedTargetValue;
		}

		const selectedDuplicateRow =
			context.selectedDuplicateRow &&
			typeof context.selectedDuplicateRow === 'object'
				? (context.selectedDuplicateRow as Record<string, unknown>)
				: null;

		if (!selectedDuplicateRow) {
			return undefined;
		}

		return {
			rowNumber:
				typeof selectedDuplicateRow.rowNumber === 'number'
					? selectedDuplicateRow.rowNumber
					: Number(selectedDuplicateRow.rowNumber) || undefined,
			sheetName:
				this.getStringValue(selectedDuplicateRow.sheetName) || undefined,
			sourceFileName:
				this.getStringValue(selectedDuplicateRow.sourceFileName) || undefined,
			uid: this.getStringValue(selectedDuplicateRow.uid) || undefined,
		};
	}

	private getEssentialMissingFieldLabel(field: string): string {
		switch (field) {
			case 'organizationId':
				return 'Organização';
			case 'organizationCnpj':
			case 'cnpj':
				return 'CNPJ';
			case 'name':
			case 'beneficiaryName':
				return 'Nome';
			case 'cpf':
			case 'beneficiaryCpf':
				return 'CPF';
			case 'birthDay':
			case 'dataNascimento':
				return 'Data de nascimento';
			case 'typeKinship':
				return 'Titularidade';
			case 'holderCpf':
				return 'CPF do titular';
			default:
				return this.formatFieldLabel(field);
		}
	}

	private getFieldDescription(field: BeneficiaryReconciliationField): string {
		switch (field) {
			case 'DUPLICATE_IN_FILE':
				return 'Incidência duplicada na base do cliente enviada.';
			case 'ACTIVE_MISSING_IN_DB':
				return 'Beneficiário presente na base do cliente, mas ainda não cadastrado na plataforma.';
			case 'INACTIVE_MISSING_IN_FILE':
				return 'Beneficiário ativo na plataforma, mas ausente na base do cliente recebida.';
			case 'ORGANIZATION_MISMATCH':
				return 'Beneficiário encontrado na plataforma, mas vinculado à sub errada.';
			case 'PLAN_MISMATCH':
				return 'Plano da plataforma diferente do plano informado na base do cliente.';
			case 'CARD_MISMATCH':
				return 'Número da carteirinha da plataforma diferente da base do cliente.';
			case 'TITULARITY_MISMATCH':
				return 'Titularidade ou parentesco divergente entre plataforma e base do cliente.';
			default:
				return field;
		}
	}

	private getFieldTitle(field: BeneficiaryReconciliationField): string {
		return getBeneficiaryReconciliationFieldLabel(field);
	}

	private getImportantMissingFieldLabel(field: string): string {
		switch (field) {
			case 'gender':
			case 'genero':
				return 'Gênero';
			case 'phone':
			case 'telefone':
				return 'Telefone';
			case 'planoSaude':
			case 'healthPlan':
			case 'healthPlanName':
				return 'Plano saúde';
			case 'carteirinhaSaude':
			case 'healthCardNumber':
				return 'Carteirinha saúde';
			case 'planoCongenere':
				return 'Plano congênere';
			case 'carteirinhaCongenere':
				return 'Carteirinha congênere';
			case 'planoConjugado':
				return 'Plano conjugado';
			case 'carteirinhaConjugado':
				return 'Carteirinha conjugado';
			case 'planoOdonto':
			case 'dentalPlan':
			case 'dentalPlanName':
				return 'Plano odonto';
			case 'carteirinhaOdonto':
			case 'dentalCardNumber':
				return 'Carteirinha odontológica';
			default:
				return this.formatFieldLabel(field);
		}
	}

	private getStringValue(value: unknown): string {
		return typeof value === 'string' ? value.trim() : '';
	}

	private hasDifferentDuplicateValues(field: keyof DuplicateRow): boolean {
		const values = this.duplicateRows
			.map((row) => this.getStringValue(row[field]).toLowerCase())
			.filter((value) => Boolean(value));

		return new Set(values).size > 1;
	}

	private isBenefitField(key: string): boolean {
		return (
			key === 'healthBenefit' ||
			key === 'beneficioSaude' ||
			key === 'hasHealthBenefit' ||
			key === 'dentalBenefit' ||
			key === 'beneficioOdonto' ||
			key === 'hasDentalBenefit'
		);
	}

	private isConjugatedBenefitContext(
		context: Record<string, unknown>,
	): boolean {
		return [
			context.benefitVariant,
			context.benefitVariantLabel,
			context.walletType,
		].some((value) =>
			['CONJUGADO', 'CONJUGATED'].includes(
				this.normalizeBenefitContextValue(value),
			),
		);
	}

	private isCongenerBenefitContext(context: Record<string, unknown>): boolean {
		return [
			context.benefitVariant,
			context.benefitVariantLabel,
			context.walletType,
		].some((value) =>
			['CONGENER', 'CONGENERE'].includes(
				this.normalizeBenefitContextValue(value),
			),
		);
	}

	private isCreationFieldListedAsMissing(label: string): boolean {
		const normalizedLabel = this.normalizeCreationMissingLabel(label);

		return this.creationMissingFieldsForDisplay.some((field) => {
			const normalizedField = this.normalizeCreationMissingLabel(field);

			return (
				normalizedField === normalizedLabel ||
				normalizedField.startsWith(`${normalizedLabel} `)
			);
		});
	}

	private isHealthCardField(key: string): boolean {
		return (
			key === 'cardNumber' ||
			key === 'healthCardNumber' ||
			key === 'carteirinhaSaude'
		);
	}

	private isDentalWalletContext(context: Record<string, unknown>): boolean {
		return this.normalizeBenefitContextValue(context.walletKind) === 'DENTAL';
	}

	private isNormalHealthBenefitContext(
		context: Record<string, unknown>,
	): boolean {
		if (
			this.isCongenerBenefitContext(context) ||
			this.isConjugatedBenefitContext(context)
		) {
			return false;
		}

		if (this.normalizeBenefitContextValue(context.walletKind) !== 'HEALTH') {
			return false;
		}

		const walletType = this.normalizeBenefitContextValue(context.walletType);
		const benefitVariant = this.normalizeBenefitContextValue(
			context.benefitVariant,
		);

		return (
			['', 'DEFAULT', 'HEALTH', 'NORMAL'].includes(walletType) &&
			['', 'DEFAULT', 'HEALTH', 'NORMAL'].includes(benefitVariant)
		);
	}

	private isCongenerMissingField(field: string): boolean {
		return this.congenerMissingFields.has(field);
	}

	private isConjugatedMissingField(field: string): boolean {
		return this.conjugatedMissingFields.has(field);
	}

	private isNormalDentalMissingField(field: string): boolean {
		return this.normalDentalMissingFields.has(field);
	}

	private isNormalHealthMissingField(field: string): boolean {
		return this.normalHealthMissingFields.has(field);
	}

	private isNormalBenefitMissingField(field: string): boolean {
		return (
			this.isNormalHealthMissingField(field) ||
			this.isNormalDentalMissingField(field)
		);
	}

	private normalizeBenefitContextValue(value: unknown): string {
		return this.getStringValue(value)
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toUpperCase();
	}

	private shouldDisplayImportantMissingField(
		field: string,
		context: Record<string, unknown>,
	): boolean {
		if (this.isCongenerBenefitContext(context)) {
			return (
				!this.isNormalBenefitMissingField(field) &&
				!this.isConjugatedMissingField(field)
			);
		}

		if (this.isConjugatedBenefitContext(context)) {
			return (
				!this.isNormalBenefitMissingField(field) &&
				!this.isCongenerMissingField(field)
			);
		}

		if (this.isDentalWalletContext(context)) {
			return !this.isNormalHealthMissingField(field);
		}

		return true;
	}

	private shouldDisplayMissingHealthBenefitFromSpreadsheet(
		context: Record<string, unknown>,
	): boolean {
		return this.isNormalHealthBenefitContext(context);
	}

	private parseApplyAction(
		value: unknown,
	): BeneficiaryReconciliationApplyAction | null {
		const action = this.getStringValue(value);

		switch (action) {
			case 'CREATE_BENEFICIARY':
			case 'IGNORE_ITEM':
			case 'INACTIVATE_BENEFICIARY':
			case 'LINK_EXISTING_BENEFICIARY':
			case 'REACTIVATE_BENEFICIARY':
			case 'SELECT_DUPLICATE_ROW':
			case 'TRANSFER_ORGANIZATION':
			case 'UPDATE_CARD':
			case 'UPDATE_PLAN':
			case 'UPDATE_PLAN_AND_CARD':
			case 'UPDATE_TITULARITY':
				return action;
			default:
				return null;
		}
	}

	private parseBenefitStatus(value: unknown): boolean | null {
		if (typeof value === 'boolean') {
			return value;
		}

		if (typeof value === 'number') {
			if (value === 1) {
				return true;
			}

			if (value === 0) {
				return false;
			}
		}

		const normalized = this.getStringValue(value).toLowerCase();

		if (!normalized) {
			return null;
		}

		if (['1', 's', 'sim', 'true', 'yes'].includes(normalized)) {
			return true;
		}

		if (
			[
				'0',
				'false',
				'n',
				'nao',
				'no',
				'não',
				'sem beneficio',
				'sem benefício',
			].includes(normalized)
		) {
			return false;
		}

		return null;
	}

	private parseMissingFieldList(rawValue: unknown): string[] {
		if (!Array.isArray(rawValue)) {
			return [];
		}

		return rawValue
			.filter((value): value is string => typeof value === 'string')
			.map((value) => value.trim())
			.filter((value) => value.length > 0);
	}

	private normalizeCreationMissingLabel(value: string): string {
		return value.split('(')[0].trim().toLocaleLowerCase('pt-BR');
	}

	private parseTypeKinship(value: unknown): TypeKinship | null {
		const normalizedValue = this.getStringValue(value).toUpperCase();

		if (!normalizedValue) {
			return null;
		}

		return (
			this.kinshipOptions.find((option) => option.value === normalizedValue)
				?.value ?? null
		);
	}

	private pickFirstKnownValue(
		source: Record<string, unknown>,
		keys: string[],
	): unknown {
		for (const key of keys) {
			if (key in source) {
				const value = source[key];
				const isBlankString = typeof value === 'string' && value.trim() === '';

				if (value !== null && value !== undefined && !isBlankString) {
					return value;
				}
			}
		}

		return null;
	}

	private renderDuplicateRowsAsPng(element: HTMLElement): string {
		const baseWidth = Math.max(element.clientWidth || 960, 720);
		const canvasWidth = Math.min(baseWidth, 1200);
		const padding = 28;
		const cardPadding = 16;
		const lineHeight = 24;
		const cardGap = 18;
		const cardTitleGap = 12;
		const cardBorderRadius = 12;
		const maxContentWidth = canvasWidth - padding * 2 - cardPadding * 2;
		const subjectName = this.duplicateSubjectName || '-';
		const subjectCpf = this.duplicateSubjectCpf || '-';
		const rows = this.duplicateRows;
		const headerLines = [
			'Linhas duplicadas encontradas',
			`Beneficiário: ${subjectName}`,
			`CPF: ${subjectCpf}`,
		];

		const scratchCanvas = document.createElement('canvas');
		const scratchContext = scratchCanvas.getContext('2d');

		if (!scratchContext) {
			throw new Error('Não foi possível preparar o canvas para download.');
		}

		scratchContext.font = '500 18px Arial';
		const wrapText = (text: string, maxWidth: number): string[] => {
			if (!text) {
				return ['-'];
			}

			const words = text.split(' ');
			const lines: string[] = [];
			let currentLine = '';

			for (const word of words) {
				const testLine = currentLine ? `${currentLine} ${word}` : word;
				const measured = scratchContext.measureText(testLine).width;

				if (measured <= maxWidth || !currentLine) {
					currentLine = testLine;
				} else {
					lines.push(currentLine);
					currentLine = word;
				}
			}

			if (currentLine) {
				lines.push(currentLine);
			}

			return lines;
		};

		const buildRowLines = (row: DuplicateRow, index: number): string[] => {
			const details = this.getDuplicateRowDetails(row);
			const lines = [
				`Opção ${index + 1}`,
				`Nome: ${row.beneficiaryName || subjectName}`,
				`CPF: ${this.formatCpf(row.beneficiaryCpf) || subjectCpf}`,
				`Origem: ${this.getDuplicateRowSource(row)}`,
				...details,
			];

			return lines.reduce<string[]>((accumulator, line) => {
				const wrapped = wrapText(line, maxContentWidth);

				return [...accumulator, ...wrapped];
			}, []);
		};

		const rowLineGroups = rows.map((row, index) => buildRowLines(row, index));
		const headerHeight = headerLines.length * lineHeight + 16;
		const cardsHeight = rowLineGroups.reduce((total, rowLines, index) => {
			const cardHeight =
				cardPadding * 2 + cardTitleGap + rowLines.length * lineHeight;

			return total + cardHeight + (index > 0 ? cardGap : 0);
		}, 0);
		const footerHeight = 46;
		const canvasHeight = Math.max(
			padding * 2 + headerHeight + cardsHeight + footerHeight,
			320,
		);
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');

		if (!context) {
			throw new Error('Não foi possível preparar o canvas para download.');
		}

		canvas.width = canvasWidth;
		canvas.height = canvasHeight;

		context.fillStyle = '#ffffff';
		context.fillRect(0, 0, canvasWidth, canvasHeight);

		let cursorY = padding;
		context.fillStyle = '#0f172a';
		context.font = '700 26px Arial';
		context.fillText(headerLines[0], padding, cursorY);
		cursorY += lineHeight + 8;

		context.font = '500 18px Arial';
		for (const line of headerLines.slice(1)) {
			context.fillText(line, padding, cursorY);
			cursorY += lineHeight;
		}

		cursorY += 8;

		for (let index = 0; index < rowLineGroups.length; index += 1) {
			const rowLines = rowLineGroups[index];
			const cardHeight =
				cardPadding * 2 + cardTitleGap + rowLines.length * lineHeight;
			const cardX = padding;
			const cardY = cursorY;
			const cardWidth = canvasWidth - padding * 2;

			context.strokeStyle = '#cbd5e1';
			context.lineWidth = 2;
			context.beginPath();
			context.roundRect(cardX, cardY, cardWidth, cardHeight, cardBorderRadius);
			context.stroke();

			let lineY = cardY + cardPadding + lineHeight;
			context.fillStyle = '#0f172a';
			context.font = '700 18px Arial';
			context.fillText(`Opção ${index + 1}`, cardX + cardPadding, lineY);
			lineY += cardTitleGap;

			context.fillStyle = '#1e293b';
			context.font = '500 17px Arial';
			for (const line of rowLines.slice(1)) {
				lineY += lineHeight;
				context.fillText(line, cardX + cardPadding, lineY);
			}

			cursorY += cardHeight + cardGap;
		}

		context.fillStyle = '#475569';
		context.font = '500 16px Arial';
		context.fillText(
			'Use este layout para validar externamente qual opção manter.',
			padding,
			canvasHeight - padding,
		);

		return canvas.toDataURL('image/png');
	}

	private resolveDefaultDuplicateRowIndex(
		targetValue: BeneficiaryReconciliationApplyTargetValue | undefined,
	): number | null {
		if (!this.isDuplicateInFile || this.duplicateRows.length === 0) {
			return null;
		}

		const defaultTargetValue =
			targetValue ?? this.getDuplicateDefaultTargetValueFromContext();

		if (!defaultTargetValue) {
			return null;
		}

		const matchedIndex = this.duplicateRows.findIndex(
			(row) =>
				(row.uid && defaultTargetValue.uid
					? row.uid === defaultTargetValue.uid
					: false) ||
				(row.rowNumber === defaultTargetValue.rowNumber &&
					row.sheetName === defaultTargetValue.sheetName &&
					row.sourceFileName === defaultTargetValue.sourceFileName),
		);

		return matchedIndex >= 0 ? matchedIndex : null;
	}

	private sanitizeCpf(value: unknown): string {
		return this.getStringValue(value).replace(/\D/g, '');
	}

	private get shouldShowCreateBeneficiaryFields(): boolean {
		return (
			this.action === 'CREATE_BENEFICIARY' &&
			!this.isDuplicateInFile &&
			!this.isActiveMissingInDatabase
		);
	}

	private toReadableLabel(value: string): string {
		return value
			.split('_')
			.map((fragment) => fragment[0] + fragment.slice(1).toLowerCase())
			.join(' ');
	}

	private triggerDownload(dataUrl: string, fileName: string): void {
		const link = document.createElement('a');

		link.href = dataUrl;
		link.download = fileName;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		link.remove();
	}
}
